// @flow

import { denormalize } from 'normalizr';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { useMemo } from 'react';
import type {
  actionTypeSingletonSliceType,
  genericSingletonStateSliceType,
  reduxAction,
} from '../dataStoreTypes';

const initialState = {};

const genericSingletonReducer = (
  actionTypeSlice: actionTypeSingletonSliceType,
) => (state: any = initialState, action: reduxAction) => {
  switch (action.type) {
    case actionTypeSlice.SINGLETON.IFNEEDED:
      return {
        ...state,
        deduplicationCounter: (state.deduplicationCounter || 0) + 1,
      };
    case actionTypeSlice.SINGLETON.PENDING:
      return {
        ...state,
        status: 1000,
        errors: {},
        deduplicationCounter: state.deduplicationCounter || 1,
      };
    case actionTypeSlice.SINGLETON.SUCCESS:
      return {
        ...state,
        id: action.id,
        status: 200,
        errors: {},
        deduplicationCounter: 0,
      };
    case actionTypeSlice.SINGLETON.ERROR:
      return {
        ...state,
        status: action.status,
        errors: action.errors,
        deduplicationCounter: 0,
      };
    default:
      return state;
  }
};

export default genericSingletonReducer;

export const genericSingletonSelectorSet = <T>(
  schema: any,
  sliceSelector: any => any,
) => ({
  get: (state: any): T =>
    denormalize(sliceSelector(state).id, schema, state.entities),
  meta: (state: any): genericSingletonStateSliceType => sliceSelector(state),
});

type genericSingletonSubscriptionHookBundleType<T> = {
  get: () => typeof undefined | T,
  meta: () => genericSingletonStateSliceType,
};

const genericSingletonMemoizedSelectorSet = (schema, sliceSelector) => ({
  get: () =>
    createSelector(
      state => sliceSelector(state).id,
      state => state.entities,
      (id, entities) => denormalize(id, schema, entities),
    ),
  meta: () =>
    createSelector(
      state => sliceSelector(state),
      meta => meta,
    ),
});

/* eslint-disable react-hooks/rules-of-hooks */
export const useGenericSingleton = <T>(
  schema: any,
  sliceSelector: any => genericSingletonStateSliceType,
): genericSingletonSubscriptionHookBundleType<T> => {
  const selectors = genericSingletonMemoizedSelectorSet(schema, sliceSelector);

  return {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    get: () => useSelector(state => useMemo(selectors.get, [])(state)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    meta: () => useSelector(state => useMemo(selectors.meta, [])(state)),
  };
};
/* eslint-enable react-hooks/rules-of-hooks */
